import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  useApiKeys,
  useCreateApiKey,
  useRevokeApiKey,
  useDeleteApiKey,
  useRotateApiKey,
  useRevealApiKey,
} from '@wordpuppi/shared';
import type { ApiKeyListItem } from '@wordpuppi/shared';
import { Eye, EyeOff, RefreshCw, Ban, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CopyButton } from '@/components/CopyButton';
import { confirmDialog } from '@/lib/dialog';
import { toast } from 'sonner';

// AB#654/655/656: API keys are per-USER, not per-site, so they live under
// /account. /sites/$siteSlug/settings/api-keys redirects here.
export const Route = createFileRoute('/account/api-keys')({
  component: ApiKeysPage,
});

function ApiKeysPage() {
  const { data: apiKeysData } = useApiKeys();
  const createApiKey = useCreateApiKey();
  const revokeApiKey = useRevokeApiKey();
  const deleteApiKey = useDeleteApiKey();
  const rotateApiKey = useRotateApiKey();
  const revealApiKey = useRevealApiKey();
  const [newKeyName, setNewKeyName] = useState('');
  // Shown-once banner — used by both create and rotate (`replaces` = old preview).
  const [newKey, setNewKey] = useState<{ key: string; replaces?: string } | null>(null);
  // Revealed secrets live in component state only, never in the query cache.
  const [revealed, setRevealed] = useState<Record<number, string>>({});

  const handleCreateKey = async () => {
    const trimmed = newKeyName.trim();
    if (!trimmed) return;
    try {
      const result = await createApiKey.mutateAsync(trimmed);
      setNewKeyName('');
      if (result.data?.key) setNewKey({ key: result.data.key });
      toast.success('API key created');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create key');
    }
  };

  const handleReveal = async (k: ApiKeyListItem) => {
    if (revealed[k.id]) {
      setRevealed(({ [k.id]: _drop, ...rest }) => rest);
      return;
    }
    try {
      const result = await revealApiKey.mutateAsync(k.id);
      if (result.data?.key) setRevealed((r) => ({ ...r, [k.id]: result.data!.key }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reveal key');
    }
  };

  const handleRotateKey = async (k: ApiKeyListItem) => {
    if (
      !(await confirmDialog(
        `Rotate "${k.name}"? A new key is issued and the current one stops working immediately.`,
        { destructive: true, confirmLabel: 'Rotate key' },
      ))
    )
      return;
    try {
      const result = await rotateApiKey.mutateAsync(k.id);
      if (result.data?.key) setNewKey({ key: result.data.key, replaces: k.key_preview });
      toast.success('API key rotated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to rotate key');
    }
  };

  const handleRevokeKey = async (k: ApiKeyListItem) => {
    if (
      !(await confirmDialog(
        `Revoke "${k.name}"? Integrations using it stop working immediately. The key stays listed as revoked; delete it to remove it.`,
        { destructive: true, confirmLabel: 'Revoke key' },
      ))
    )
      return;
    try {
      await revokeApiKey.mutateAsync(k.id);
      toast.success('API key revoked');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to revoke key');
    }
  };

  const handleDeleteKey = async (k: ApiKeyListItem) => {
    if (
      !(await confirmDialog(
        `Delete "${k.name}" permanently? This removes it from the list and cannot be undone.`,
        { destructive: true, confirmLabel: 'Delete key' },
      ))
    )
      return;
    try {
      await deleteApiKey.mutateAsync(k.id);
      toast.success('API key deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete key');
    }
  };

  const apiKeys: ApiKeyListItem[] = apiKeysData?.data || [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>API Access</CardTitle>
          <CardDescription>
            Manage API keys for MCP servers, CI pipelines, n8n, and other integrations.
            Each key grants full access to your account.
            <span className="block mt-1">
              Revoke disables a key but keeps it listed; Delete removes it permanently.
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {newKey && (
            <div className="p-3 border border-primary/30 bg-primary/10 rounded-lg space-y-2">
              <p className="text-sm font-medium">Save your new API key now — it won't be shown again.</p>
              {newKey.replaces && (
                <p className="text-xs text-muted-foreground">
                  Replaces <span className="font-mono">{newKey.replaces}</span>
                </p>
              )}
              <div className="flex gap-2">
                <Input type="text" value={newKey.key} readOnly className="font-mono text-sm" />
                <CopyButton text={newKey.key} />
              </div>
              <Button variant="ghost" size="sm" onClick={() => setNewKey(null)}>
                Dismiss
              </Button>
            </div>
          )}

          {apiKeys.length > 0 && (
            <div className="space-y-2">
              {apiKeys.map((k) => (
                <div key={k.id} className="rounded-lg border p-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <div className={'min-w-0 flex-1' + (k.is_active ? '' : ' opacity-60')}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{k.name}</span>
                        {!k.is_active && <Badge variant="secondary">Revoked</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <span className="font-mono">{k.key_preview}</span>
                        {k.last_used_at && (
                          <> · Last used {new Date(k.last_used_at).toLocaleDateString()}</>
                        )}
                        <> · Created {new Date(k.created_at).toLocaleDateString()}</>
                      </div>
                      {!k.is_active && (
                        <div className="text-xs text-muted-foreground">
                          Revoked — no longer works · Delete to remove
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap justify-end gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReveal(k)}
                        disabled={revealApiKey.isPending}
                      >
                        {revealed[k.id] ? (
                          <EyeOff className="h-3.5 w-3.5" aria-hidden />
                        ) : (
                          <Eye className="h-3.5 w-3.5" aria-hidden />
                        )}
                        {revealed[k.id] ? 'Hide' : 'View'}
                      </Button>
                      {k.is_active && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRotateKey(k)}
                            disabled={rotateApiKey.isPending}
                          >
                            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                            Rotate
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevokeKey(k)}
                            disabled={revokeApiKey.isPending}
                          >
                            <Ban className="h-3.5 w-3.5" aria-hidden />
                            Revoke
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDeleteKey(k)}
                        disabled={deleteApiKey.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                        Delete
                      </Button>
                    </div>
                  </div>
                  {revealed[k.id] && (
                    <div className="flex gap-2 mt-2">
                      <Input
                        type="text"
                        value={revealed[k.id]}
                        readOnly
                        aria-label={`Full API key for ${k.name}`}
                        className="font-mono text-sm"
                      />
                      <CopyButton text={revealed[k.id]!} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <Input
              type="text"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="Key name (e.g. MCP Server, CI Pipeline)"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateKey();
                }
              }}
            />
            <Button onClick={handleCreateKey} disabled={createApiKey.isPending || !newKeyName.trim()}>
              {createApiKey.isPending ? 'Creating...' : 'Create Key'}
            </Button>
          </div>

          <div className="space-y-3 pt-2">
            <p className="text-sm font-medium">Usage Examples</p>

            <div>
              <p className="text-xs text-muted-foreground mb-1">
                MCP server config (Claude Code, Cursor, etc.):
              </p>
              <pre className="p-3 bg-muted rounded text-xs overflow-x-auto">
{`{
  "mcpServers": {
    "wordpuppi": {
      "command": "wpp-mcp",
      "args": ["serve", "--api-key", "<your-api-key>"]
    }
  }
}`}
              </pre>
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-1">
                Two-step API access (curl, n8n, Make, Postman):
              </p>
              <pre className="p-3 bg-muted rounded text-xs overflow-x-auto">
{`# 1. Exchange API key for a JWT token
curl -X POST http://localhost:5150/api/auth/token \\
  -H "Content-Type: application/json" \\
  -d '{"api_key": "YOUR_API_KEY"}'

# 2. Use the JWT token for API calls
curl http://localhost:5150/api/sites \\
  -H "Authorization: Bearer <token from step 1>"`}
              </pre>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
